/**
 * STARSEED-CAPMARKETS-257 — the disclaimer prompt, shared by the two pages a
 * member lands on (`/dashboard` after a login, `/command-center` after
 * registration), and the return path the disclaimer page honours afterwards.
 *
 * One predicate in one place: both landing pages read the same
 * `GET /command-center/me` payload and must answer "does this member owe a
 * disclaimer acknowledgement?" identically. The backend computes the
 * "current version acknowledged" flag (`compute_checklist` compares against
 * `DISCLAIMER_VERSION`, so an older acknowledgement counts as missing); the
 * client keys on that flag only.
 */

export const DEFAULT_RETURN_PATH = "/command-center";

/**
 * The checklist key the backend uses for the disclaimer item
 * (`services/onboarding_checklist.py`). `disclaimerGate.test.js` reads that
 * file and fails if the key is renamed, so the gate cannot silently stop
 * firing on a payload re-key.
 */
export const DISCLOSURES_KEY = "disclosures";

const SESSION_KEY_PREFIX = "capmarkets.disclaimer.acknowledged:";
const sessionKey = (userId) => `${SESSION_KEY_PREFIX}${userId}`;

/**
 * Remember, for this browser session, that the member recorded an
 * acknowledgement. The gate then stands down for the session even if the
 * checklist still reports the item open — which the backend does on purpose
 * when its own acknowledgement read fails (it degrades the item to not-done
 * rather than let the handoff gate pass), and for one request when a
 * `DISCLAIMER_VERSION` bump races a page that was already open. Without
 * this, a member who has just acknowledged is sent straight back to the
 * disclaimer for as long as the degradation lasts. The checklist row stays
 * open and the backend still holds the real gates (agreement, handoff), so
 * nothing legal depends on this flag.
 */
export function markDisclaimerAcknowledged(userId) {
  if (!userId) return;
  try {
    sessionStorage.setItem(sessionKey(userId), "1");
  } catch {
    // Storage unavailable — the gate simply prompts again.
  }
}

/**
 * Called wherever a session ends — explicit logout, the api client's 401
 * handler, a failed auth bootstrap: a flag is a statement about the member
 * who set it, and the next member on this tab — or the same member after a
 * version bump deployed in between — must be prompted afresh.
 */
export function forgetDisclaimerAcknowledgements() {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(SESSION_KEY_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing was stored either.
  }
}

export function acknowledgedThisSession(userId) {
  if (!userId) return false;
  try {
    return sessionStorage.getItem(sessionKey(userId)) === "1";
  } catch {
    return false;
  }
}

/** The disclaimer page with a return path; the gate's destination. */
export function disclaimerPath(next) {
  return `/disclaimer?next=${next}`;
}

/**
 * Where to send the member instead of the page, or `null` to let the page
 * render. Applies to `client` only — the disclaimer is a member obligation;
 * staff, partner and referrer roles are never routed. A payload without the
 * item (an older backend, a failed call rendered as `[]`) fails open: the
 * page renders, and the backend gates the agreement regardless.
 */
export function disclaimerRedirect(user, items, next) {
  if (user?.role !== "client") return null;
  const row = Array.isArray(items) ? items.find((i) => i?.key === DISCLOSURES_KEY) : null;
  if (!row || row.done) return null;
  if (acknowledgedThisSession(user.id)) return null;
  return disclaimerPath(next);
}

/**
 * The `next` parameter if it is one this module will honour, else `null`.
 * Only a same-origin path is honoured: it must start with exactly one `/`
 * (not `//`, not `/\`), and after the browser's own URL parsing it must
 * still resolve on this origin — that second check is what stops
 * `/\evil.example` and `/%09/evil`, which browsers read as `//evil...`,
 * from ever reaching `navigate`.
 */
function honouredReturnPath(search, origin) {
  const raw = new URLSearchParams(search || "").get("next");
  if (!raw || !origin || !/^\/(?![/\\])/.test(raw)) return null;
  try {
    const url = new URL(raw, origin);
    if (url.origin !== origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    // Re-check the NORMALISED path: `/..//evil.example` resolves on this
    // origin with the protocol-relative pathname `//evil.example`, which a
    // future `<a href>` consumer would treat as another host.
    return /^\/(?![/\\])/.test(path) ? path : null;
  } catch {
    return null;
  }
}

/**
 * Where to send a member after a RECORDED acknowledgement: the honoured
 * `next`, else the checklist — the pre-257 destination.
 */
export function returnPathFromSearch(search, origin = globalThis.location?.origin) {
  return honouredReturnPath(search, origin) ?? DEFAULT_RETURN_PATH;
}

/**
 * Whether a landing-page gate sent the member here with a return path this
 * module will actually honour. The disclaimer page keys its "you will be
 * returned to where you were" notice on this rather than on the mere
 * presence of `next`: a crafted `?next=//evil.example` is rejected by
 * `returnPathFromSearch` and falls back to the checklist, so promising a
 * return would be telling the reader something untrue (Copilot, #2596).
 */
export function hasHonouredReturnPath(search, origin = globalThis.location?.origin) {
  return honouredReturnPath(search, origin) !== null;
}
